import { useMemo, useState } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { AttributeRenderer, DekoxIcon, useAttributeGridForm } from "src/components";
import { useConfiguratorPageT } from "src/translation-hooks/configurator-page";
import { Model_ConfigurationGet_SystemProductConfigurationItem } from "src/backend/openapi/v1";
import { configureProductActions } from "../../configure-product-store";
import { findRootAncestor } from "src/pages/configurator/configurator-utils";

type Props = {
  items: Model_ConfigurationGet_SystemProductConfigurationItem[];
  selectedProjectLineItemId: string | undefined;
  updateProjectLineItemAttributeRequest: typeof configureProductActions.updateProjectLineItemAttributeRequest;
  onAttributeChanged: () => void;
};

export const SharedAttributesSection = ({ items, selectedProjectLineItemId, updateProjectLineItemAttributeRequest, onAttributeChanged }: Props) => {
  const configuratorPageT = useConfiguratorPageT();

  const theme = useTheme();
  const [expanded, setExpanded] = useState(true);

  // SharedAttribute values are always stored against the tree's root item, no matter which item is
  // currently selected, so resolve the root here and read/write its attributes independently of the
  // selected item's own attributes
  const rootItem = useMemo(() => {
    if (!selectedProjectLineItemId) return undefined;
    return findRootAncestor(items, selectedProjectLineItemId);
  }, [items, selectedProjectLineItemId]);

  const rootItemId = rootItem?.systemProductConfigurationItemId;

  const sharedAttributesForm = useAttributeGridForm(
    rootItem?.attributes,
    ({ attributeId, value, shouldDebounce, onError }) => {
      if (!rootItemId) return;
      updateProjectLineItemAttributeRequest({ attributeId, value, systemProductConfigurationItemId: rootItemId }, shouldDebounce, onError);
    },
    onAttributeChanged
  );

  if (sharedAttributesForm.rows.length === 0) return null;

  // error/componentError are "string | undefined", and an invalid-but-no-message row (the !isValid
  // branch in use-attribute-grid-form.ts) deliberately sets error to "" rather than a real message —
  // so presence (!== undefined) must be checked here, not string truthiness
  const hasInvalidAttributes = sharedAttributesForm.rows.some(row => row.formState.value.error !== undefined || row.formState.value.componentError !== undefined);

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
      disableGutters
      sx={{
        boxShadow: "none",
        bgcolor: theme.palette.customColors.white,
        margin: theme.spacing(4),
        marginBottom: 0,
        width: 300,
        // MUI's AccordionRoot unconditionally zeroes marginTop/marginBottom via
        // "&.Mui-expanded:first-of-type"/"&.Mui-expanded:last-of-type" (meant to avoid double-margins in a
        // stacked accordion group). This lone accordion matches both selectors at once, so a plain
        // "&.Mui-expanded" override loses the cascade (fewer selectors = lower specificity) — match the
        // exact selectors to win
        "&.Mui-expanded:first-of-type": { marginTop: theme.spacing(4) },
        "&.Mui-expanded:last-of-type": { marginBottom: 0 }
      }}
    >
      <AccordionSummary expandIcon={<DekoxIcon icon="expand" />}>
        <Stack direction="row" alignItems="center">
          <Typography variant="body2" color={theme.palette.customColors.darkGrey}>
            {configuratorPageT.baseSetup}
          </Typography>
          <Tooltip title={configuratorPageT.isInvalidInformation} arrow placement="top">
            <DekoxIcon icon="errorOutline" fontSize="small" color="error" sx={{ ml: 1, visibility: hasInvalidAttributes && !expanded ? "visible" : "hidden" }} />
          </Tooltip>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack gap={2}>
          {sharedAttributesForm.rows.map(formItem => (
            <AttributeRenderer key={formItem.formState.attributeId.value} formItem={formItem} handleChange={sharedAttributesForm.handleChange} />
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};
